import { createContext, type FunctionComponent, type PropsWithChildren, useContext, useState } from 'react';
import { useStore } from 'zustand';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import type { ProfileTaskParentContext } from '../model/profile-task';
import {
    createProfileTaskDraftCoordinator,
    getProfileTaskParentDraftKey,
    type ProfileTaskDraftCoordinator,
    type ProfileTaskParentDraftState,
} from './profile-task-draft-coordinator';

const ProfileTaskDraftCoordinatorContext = createContext<ProfileTaskDraftCoordinator | undefined>(undefined);

export const ProfileTaskDraftCoordinatorProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [coordinator] = useState(createProfileTaskDraftCoordinator);

    return (
        <ProfileTaskDraftCoordinatorContext.Provider value={coordinator}>
            {children}
        </ProfileTaskDraftCoordinatorContext.Provider>
    );
};

export const useProfileTaskDraftCoordinator = (): ProfileTaskDraftCoordinator => {
    const coordinator = useContext(ProfileTaskDraftCoordinatorContext);
    if (coordinator === undefined) {
        throw new Error('Profile task draft coordinator is unavailable outside its URL-tile surface');
    }
    return coordinator;
};

export const useProfileTaskParentDraft = (
    context: ProfileTaskParentContext,
): ProfileTaskParentDraftState | undefined => {
    const coordinator = useProfileTaskDraftCoordinator();
    const key = getProfileTaskParentDraftKey(context);
    return useStore(coordinator, (state) => state.parents.get(key));
};

export const useProfileTaskParentLanguage = (context: ProfileTaskParentContext): LanguageCodeForJSA | undefined => {
    const coordinator = useProfileTaskDraftCoordinator();
    const key = getProfileTaskParentDraftKey(context);
    return useStore(coordinator, (state) => state.parentLanguages.get(key));
};

const EMPTY_PENDING_TITLES: ReadonlyMap<string, string> = new Map();

export const usePendingProfileTaskTitles = (): ReadonlyMap<string, string> => {
    const coordinator = useProfileTaskDraftCoordinator();
    // The fallback keeps hot-reloaded sessions alive whose store predates this field.
    return useStore(coordinator, (state) => state.pendingTaskTitles ?? EMPTY_PENDING_TITLES);
};
